import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import { FaSave, FaArrowLeft, FaInfoCircle, FaUsers } from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import TestService from "../../../services/testService";
import CourseService from "../../../services/courseService";
import BatchService from "../../../services/batchService";

const CreateTest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState(0);
  const [batchStudentCount, setBatchStudentCount] = useState(0);
  const [loadingBatchCount, setLoadingBatchCount] = useState(false);
  const [maxAttempts, setMaxAttempts] = useState(1);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category_id: "",
    course_id: "",
    batch_id: "",
    total_questions: 10,
    duration_minutes: 30,
    passing_percentage: 60,
    status: "draft",
    is_scheduled: false,
    start_date: "",
    end_date: "",
  });

  const [assignmentType, setAssignmentType] = useState("none");

  useEffect(() => {
    loadCategories();
    loadCourses();
    loadBatches();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      loadAvailableQuestions();
    }
  }, [formData.category_id]);

  // Get batch student count when batch changes
  useEffect(() => {
    if (assignmentType === "batch" && formData.batch_id) {
      loadBatchStudentCount(formData.batch_id);
    } else {
      setBatchStudentCount(0);
    }
  }, [formData.batch_id, assignmentType]);

  const loadCategories = async () => {
    try {
      const response = await TestService.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error("Failed to load categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const loadCourses = async () => {
    try {
      const response = await CourseService.getAllCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error("Failed to load courses:", error);
    }
  };

  const loadBatches = async () => {
    try {
      const response = await BatchService.getAllBatches();
      setBatches(response.data || []);
    } catch (error) {
      console.error("Failed to load batches:", error);
    }
  };

  const loadAvailableQuestions = async () => {
    try {
      const response = await TestService.getQuestionCount(formData.category_id);
      setAvailableQuestions(response.data?.count || 0);
    } catch (error) {
      console.error("Failed to load question count:", error);
    }
  };

  const loadBatchStudentCount = async (batchId) => {
    if (!batchId) return;

    try {
      setLoadingBatchCount(true);
      const response = await BatchService.getBatchStudents(batchId);

      let students = [];
      if (response?.data && Array.isArray(response.data)) {
        students = response.data;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        students = response.data.data;
      } else if (response?.data?.students && Array.isArray(response.data.students)) {
        students = response.data.students;
      }

      setBatchStudentCount(students.length);
    } catch (error) {
      console.error("Failed to load batch student count:", error);
      setBatchStudentCount(0);
    } finally {
      setLoadingBatchCount(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.category_id) {
      toast.error("Please select a category");
      return;
    }
    if (formData.total_questions > availableQuestions) {
      toast.error(
        `Only ${availableQuestions} questions available in this category`,
      );
      return;
    }

    // Validate scheduled dates
    if (formData.is_scheduled) {
      if (!formData.start_date) {
        toast.error("Start date is required for scheduled test");
        return;
      }
      if (!formData.end_date) {
        toast.error("End date is required for scheduled test");
        return;
      }
      if (new Date(formData.start_date) >= new Date(formData.end_date)) {
        toast.error("End date must be after start date");
        return;
      }
    }

    // For batch assignment, validate that batch has students
    if (assignmentType === "batch" && formData.batch_id) {
      if (batchStudentCount === 0) {
        toast.error("This batch has no students to assign. Please add students to the batch first.");
        return;
      }
    }

    const submitData = { ...formData };

    if (assignmentType === "course") {
      submitData.batch_id = null;
    } else if (assignmentType === "batch") {
      submitData.course_id = null;
      submitData.max_attempts = maxAttempts;
      // No need to send selected_students - backend will auto-assign all batch students
    } else {
      submitData.course_id = null;
      submitData.batch_id = null;
    }

    // Remove is_scheduled flag, use start_date/end_date presence to determine
    if (!formData.is_scheduled) {
      submitData.start_date = null;
      submitData.end_date = null;
    }

    try {
      setLoading(true);
      await TestService.createTest(submitData);
      toast.success("Test created successfully and assigned to all batch students");
      navigate("/admin/tests");
    } catch (error) {
      console.error("Failed to create test:", error);
      toast.error(error.response?.data?.message || "Failed to create test");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        <Button
          variant="link"
          className="text-decoration-none mb-3"
          onClick={() => navigate("/admin/tests")}
        >
          <FaArrowLeft className="me-2" /> Back to Tests
        </Button>

        <Card className="shadow-sm">
          <Card.Header className="bg-primary text-white">
            <h4 className="mb-0">Create New Test</h4>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Test Title *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., JavaScript Basics Quiz"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Description / Instructions</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="Instructions for students..."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Question Category *</Form.Label>
                    <Form.Select
                      value={formData.category_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          category_id: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name} ({cat.total_questions || 0} questions)
                        </option>
                      ))}
                    </Form.Select>
                    {formData.category_id && (
                      <Form.Text className="text-muted">
                        <FaInfoCircle className="me-1" />
                        Available questions: {availableQuestions}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Total Questions *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max={availableQuestions || 100}
                          value={formData.total_questions}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              total_questions: parseInt(e.target.value) || 1,
                            })
                          }
                          required
                        />
                        <Form.Text className="text-muted">
                          Questions will be randomly selected from the category
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration (minutes) *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="180"
                          value={formData.duration_minutes}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              duration_minutes: parseInt(e.target.value) || 1,
                            })
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Passing Percentage *</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          value={formData.passing_percentage}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              passing_percentage: parseInt(e.target.value) || 0,
                            })
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={formData.status}
                          onChange={(e) =>
                            setFormData({ ...formData, status: e.target.value })
                          }
                        >
                          <option value="draft">
                            Draft (Not visible to students)
                          </option>
                          <option value="published">
                            Published (Visible to students)
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Scheduled Test Section */}
                  <Form.Group className="mb-3">
                    <Form.Label>Schedule Test (Optional)</Form.Label>
                    <Form.Check
                      type="checkbox"
                      label="Schedule this test (set start and end time)"
                      checked={formData.is_scheduled}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_scheduled: e.target.checked,
                        })
                      }
                    />
                  </Form.Group>

                  {formData.is_scheduled && (
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Date & Time *</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={formData.start_date}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                start_date: e.target.value,
                              })
                            }
                            required={formData.is_scheduled}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>End Date & Time *</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={formData.end_date}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                end_date: e.target.value,
                              })
                            }
                            required={formData.is_scheduled}
                          />
                          <Form.Text className="text-muted">
                            Test will not be accessible after this time
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>Assign Test To</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Not Assigned (Manual)"
                        name="assignmentType"
                        value="none"
                        checked={assignmentType === "none"}
                        onChange={(e) => setAssignmentType(e.target.value)}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Assign to Course"
                        name="assignmentType"
                        value="course"
                        checked={assignmentType === "course"}
                        onChange={(e) => setAssignmentType(e.target.value)}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Assign to Batch (All Students)"
                        name="assignmentType"
                        value="batch"
                        checked={assignmentType === "batch"}
                        onChange={(e) => setAssignmentType(e.target.value)}
                      />
                    </div>
                  </Form.Group>

                  {assignmentType === "course" && (
                    <Form.Group className="mb-3">
                      <Form.Label>Select Course *</Form.Label>
                      <Form.Select
                        value={formData.course_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            course_id: e.target.value,
                          })
                        }
                        required={assignmentType === "course"}
                      >
                        <option value="">Select Course</option>
                        {courses.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        All students enrolled in this course will get this test
                      </Form.Text>
                    </Form.Group>
                  )}

                  {assignmentType === "batch" && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Select Batch *</Form.Label>
                        <Form.Select
                          value={formData.batch_id}
                          onChange={(e) => {
                            setFormData({ ...formData, batch_id: e.target.value });
                          }}
                          required={assignmentType === "batch"}
                        >
                          <option value="">Select Batch</option>
                          {batches.map((batch) => (
                            <option key={batch.id} value={batch.id}>
                              {batch.name} ({batch.course_name})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      {/* Max Attempts Setting */}
                      <Form.Group className="mb-3">
                        <Form.Label>Maximum Attempts per Student</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="10"
                          value={maxAttempts}
                          onChange={(e) =>
                            setMaxAttempts(parseInt(e.target.value) || 1)
                          }
                        />
                        <Form.Text className="text-muted">
                          Number of times a student can attempt this test (default: 1)
                        </Form.Text>
                      </Form.Group>

                      {/* Student Count Info - Auto Assignment */}
                      {formData.batch_id && (
                        <Alert variant="info" className="mt-2">
                          <FaUsers className="me-2" />
                          <strong>Auto-Assignment:</strong> All students in this batch will be automatically assigned this test.
                          {loadingBatchCount ? (
                            <Spinner animation="border" size="sm" className="ms-2" />
                          ) : (
                            batchStudentCount > 0 && (
                              <span className="ms-2">
                                ({batchStudentCount} student{batchStudentCount !== 1 ? 's' : ''} will be assigned)
                              </span>
                            )
                          )}
                        </Alert>
                      )}
                    </>
                  )}

                  <Alert variant="info" className="mt-3">
                    <strong>Note:</strong> When students start this test,{" "}
                    <strong>{formData.total_questions}</strong> questions will
                    be randomly selected from the{" "}
                    <strong>
                      {categories.find((c) => c.id === formData.category_id)
                        ?.name || "selected"}
                    </strong>{" "}
                    category. Each student may get a different set of questions.
                    {formData.is_scheduled && (
                      <>
                        <br />
                        <strong>⏰ Scheduled:</strong> This test will be
                        available from{" "}
                        {new Date(formData.start_date).toLocaleString()} to{" "}
                        {new Date(formData.end_date).toLocaleString()}
                      </>
                    )}
                    {assignmentType === "batch" && formData.batch_id && (
                      <>
                        <br />
                        <strong>👥 Batch Assignment:</strong> Test will be automatically assigned to{" "}
                        <strong>{batchStudentCount}</strong> student{batchStudentCount !== 1 ? 's' : ''} in this batch.
                      </>
                    )}
                  </Alert>
                </Col>

                <Col md={4}>
                  <Card className="bg-light">
                    <Card.Body>
                      <h6>Summary</h6>
                      <hr />
                      <p>
                        <strong>Total Questions:</strong>{" "}
                        {formData.total_questions}
                      </p>
                      <p>
                        <strong>Duration:</strong> {formData.duration_minutes}{" "}
                        minutes
                      </p>
                      <p>
                        <strong>Passing Score:</strong>{" "}
                        {formData.passing_percentage}%
                      </p>
                      <p>
                        <strong>Status:</strong>{" "}
                        {formData.status === "draft" ? "Draft" : "Published"}
                      </p>
                      <p>
                        <strong>Assigned To:</strong>{" "}
                        {assignmentType === "course"
                          ? "Course"
                          : assignmentType === "batch"
                            ? "Batch (All Students)"
                            : "Not Assigned"}
                      </p>
                      {assignmentType === "batch" && formData.batch_id && (
                        <p>
                          <strong>Students to Assign:</strong>{" "}
                          {batchStudentCount} student{batchStudentCount !== 1 ? 's' : ''}
                        </p>
                      )}
                      {assignmentType === "batch" && (
                        <p>
                          <strong>Max Attempts:</strong> {maxAttempts} per
                          student
                        </p>
                      )}
                      {formData.is_scheduled && (
                        <>
                          <p>
                            <strong>Start Date:</strong>{" "}
                            {formData.start_date
                              ? new Date(formData.start_date).toLocaleString()
                              : "Not set"}
                          </p>
                          <p>
                            <strong>End Date:</strong>{" "}
                            {formData.end_date
                              ? new Date(formData.end_date).toLocaleString()
                              : "Not set"}
                          </p>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="secondary"
                  onClick={() => navigate("/admin/tests")}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaSave className="me-2" />
                  )}
                  {loading ? "Creating..." : "Create Test"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default CreateTest;